import { parseRetryAfterMs } from './shared/request-lifecycle.js';
import {
	getElementsByInputName,
	readInputValue,
} from './shared/input-values.js';
import {
	buildPayloadSignature,
	toBoolean,
	toInteger,
	toNumber,
} from './shared/payload.js';
import {
	clearResultMarkupCache,
	readResultMarkupCache,
	writeResultMarkupCache,
} from './shared/result-cache.js';
import { dispatchStepStateChange } from './shared/step-state.js';

const REQUIRED_HIDDEN_KEYS = [
	'sd_advice_ctx',
	'sd_advice_status',
	'sd_advice_hash',
	'sd_advice_fetched_at',
];
const MACHINE_ADVICE_ACTIVE_CLASS = 'machine-advice-active';
const MACHINE_ADVICE_ACTIVE_TARGET_SELECTORS = [
	'.gfield--type-name',
	'.gfield--type-email',
	'.sd-configurator__company',
];
const ADVICE_REQUEST_SEQ_DATA_KEY = 'sdConfigAdviceRequestSeq';
const ADVICE_REQUEST_ACTIVE_DATA_KEY = 'sdConfigAdviceRequestActive';
const ADVICE_PAYLOAD_SIGNATURE_DATA_KEY = 'sdConfigAdvicePayloadSignature';
const ADVICE_LAST_ATTEMPT_SIGNATURE_DATA_KEY = 'sdConfigAdviceLastAttemptSignature';
const ADVICE_NO_RESULTS_DATA_KEY = 'sdConfigAdviceNoResults';
const adviceMarkupCacheByForm = new Map();
const adviceActiveAbortControllerByForm = new Map();
const registeredPreSubmissionFilterForms = new Set();
const DEFAULT_LOADING_DELAY_MS = 2000;
const DEFAULT_REQUEST_TIMEOUT_MS = 15000;
const DEFAULT_RETRY_DELAY_MS = 500;
const MAX_RETRY_DELAY_MS = 2500;

function resolveLoadingDelayMs(config) {
	const parsed = Number.parseInt(String(config?.loadingDelayMs ?? DEFAULT_LOADING_DELAY_MS), 10);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return DEFAULT_LOADING_DELAY_MS;
	}

	return parsed;
}

function waitForMinimumLoadingDelay(config) {
	const delayMs = resolveLoadingDelayMs(config);
	if (delayMs <= 0) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		window.setTimeout(resolve, delayMs);
	});
}

/**
* Responsibility:
* - Resolve the configured machine-advice request timeout in milliseconds.
*
* Touches:
* - Reads `config.adviceRequestTimeoutMs` with fallback to `DEFAULT_REQUEST_TIMEOUT_MS`.
*
* Guarantees:
* - Returns a non-negative finite integer timeout.
*/
function resolveRequestTimeoutMs(config) {
	const parsed = Number.parseInt(String(config?.adviceRequestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS), 10);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return DEFAULT_REQUEST_TIMEOUT_MS;
	}

	return parsed;
}

/**
* Responsibility:
* - Delay execution for lightweight retry/backoff orchestration.
*
* Touches:
* - Uses `window.setTimeout()` to resolve after the provided delay.
*
* Guarantees:
* - Resolves immediately for non-positive values.
*/
function sleepMs(delayMs) {
	const safeDelay = Number.isFinite(delayMs) ? Number(delayMs) : 0;
	if (safeDelay <= 0) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		window.setTimeout(resolve, safeDelay);
	});
}

/**
* Responsibility:
* - Build bounded retry delay for transient advice request failures.
*
* Touches:
* - Uses `error.retryAfterMs` when provided, otherwise exponential backoff with jitter.
*
* Guarantees:
* - Returns a bounded, non-negative delay in milliseconds.
*/
function resolveRetryDelayMs(error, attempt) {
	const retryAfterMs = Number(error?.retryAfterMs || 0);
	if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) {
		return Math.min(retryAfterMs, MAX_RETRY_DELAY_MS);
	}

	const safeAttempt = Number.isFinite(attempt) && attempt > 0 ? Math.trunc(attempt) : 1;
	const backoff = DEFAULT_RETRY_DELAY_MS * (2 ** (safeAttempt - 1));
	const jitter = Math.floor(Math.random() * 250);

	return Math.min(backoff + jitter, MAX_RETRY_DELAY_MS);
}

/**
* Responsibility:
* - Represent machine-advice request failures with stable metadata.
*
* Touches:
* - Used by `requestResult()` and consumed in `resultAtAdviceStep()` error handling.
*
* Guarantees:
* - Preserves status/transient/traceId/retryAfterMs plus failure diagnostics on a standard `Error`.
*/
class AdviceRequestError extends Error {
	constructor(message, options = {}) {
		super(message);
		this.name = 'AdviceRequestError';
		this.status = Number(options.status || 0);
		this.transient = options.transient === true;
		this.traceId = String(options.traceId || '');
		this.retryAfterMs = Number(options.retryAfterMs || 0);
		this.failureCode = String(options.failureCode || '');
		this.failureStage = String(options.failureStage || '');
		this.whatWentWrong = String(options.whatWentWrong || '');
		this.likelyCause = String(options.likelyCause || '');
		this.howToFix = String(options.howToFix || '');
		this.providerHttpCode = Number(options.providerHttpCode || 0);
		this.providerMessage = String(options.providerMessage || '');
	}
}

function getForm(config) {
	return document.getElementById(`gform_${config.formId}`);
}

function setMachineAdviceActiveState(form, active) {
	if (!(form instanceof HTMLElement)) {
		return;
	}

	const targets = form.querySelectorAll(MACHINE_ADVICE_ACTIVE_TARGET_SELECTORS.join(', '));
	targets.forEach((target) => {
		target.classList.toggle(MACHINE_ADVICE_ACTIVE_CLASS, Boolean(active));
	});
}

function getPageNumber(page) {
	if (!(page instanceof Element)) {
		return 0;
	}

	const id = page.getAttribute('id') || '';
	const match = id.match(/gform_page_\d+_(\d+)/);
	if (!match) {
		return 0;
	}

	return Number.parseInt(match[1], 10) || 0;
}

function findBoundaryPageNumbers(form, boundaryClass) {
	const normalizedBoundaryClass = typeof boundaryClass === 'string'
		? boundaryClass.trim()
		: '';
	if (normalizedBoundaryClass === '') {
		return [];
	}

	const pages = Array.from(form.querySelectorAll('.gform_page'));
	const boundaryPages = pages
		.filter((page) => page.classList.contains(normalizedBoundaryClass) || page.querySelector(`.${normalizedBoundaryClass}`) !== null)
		.map((page) => getPageNumber(page))
		.filter((number) => number > 0);

	return Array.from(new Set(boundaryPages)).sort((a, b) => a - b);
}

function getBoundaryPage(form, boundaryClass) {
	const normalizedBoundaryClass = typeof boundaryClass === 'string'
		? boundaryClass.trim()
		: '';
	if (normalizedBoundaryClass === '') {
		return null;
	}

	const pages = Array.from(form.querySelectorAll('.gform_page'));
	for (const page of pages) {
		if (page.classList.contains(normalizedBoundaryClass)) {
			return page;
		}

		if (page.querySelector(`.${normalizedBoundaryClass}`) !== null) {
			return page;
		}
	}

	return null;
}

function findAdvicePages(form, containerClass) {
	const normalizedContainerClass = typeof containerClass === 'string'
		? containerClass.trim()
		: '';
	if (normalizedContainerClass === '') {
		return [];
	}

	const pages = Array.from(form.querySelectorAll('.gform_page'));
	return pages.filter((page) => page.querySelector(`.${normalizedContainerClass}`) !== null);
}

function getAdvicePage(form, containerClass) {
	const advicePages = findAdvicePages(form, containerClass);
	if (advicePages.length === 0) {
		return null;
	}

	return advicePages[0];
}

function getBoundaryNextButton(form, boundaryClass) {
	const page = getBoundaryPage(form, boundaryClass);
	if (!page) {
		return null;
	}

	const nextButton = page.querySelector('.gform_next_button');
	if (nextButton instanceof HTMLButtonElement || nextButton instanceof HTMLInputElement) {
		return nextButton;
	}

	return null;
}

function getAdviceStepContainer(form, config) {
	const advicePage = getAdvicePage(form, config.adviceContainerClass);
	if (!advicePage) {
		return null;
	}

	return getAdviceContainer(advicePage, config.adviceContainerClass);
}

function getAdviceStepNextButton(form, config) {
	const advicePage = getAdvicePage(form, config.adviceContainerClass);
	if (!advicePage) {
		return null;
	}

	const nextButton = advicePage.querySelector('.gform_next_button');
	if (nextButton instanceof HTMLButtonElement || nextButton instanceof HTMLInputElement) {
		return nextButton;
	}

	return null;
}

function setAdviceStepNextDisabled(form, config, disabled) {
	const nextButton = getAdviceStepNextButton(form, config);
	if (!(nextButton instanceof HTMLButtonElement || nextButton instanceof HTMLInputElement)) {
		return;
	}

	nextButton.disabled = Boolean(disabled);
	nextButton.dataset.sdConfigNoResultsBlocked = disabled ? '1' : '0';
}

function setButtonLoading(button, loading) {
	button.dataset.sdConfigLoading = loading ? '1' : '0';
}

function readButtonLabel(button) {
	if (button instanceof HTMLInputElement) {
		return String(button.value || '').trim();
	}

	return String(button.textContent || '').trim();
}

function writeButtonLabel(button, label) {
	if (button instanceof HTMLInputElement) {
		button.value = label;
		return;
	}

	button.textContent = label;
}

function getConfiguredLoadingLabel(config) {
	const loadingLabel = String(config.adviceLoadingText || '').trim();
	return loadingLabel !== '' ? loadingLabel : 'Fetching advice...';
}

function setBoundaryButtonState(app, form, state, context = {}) {
	const button = getBoundaryNextButton(form, app.config.boundaryClass);
	if (!button) {
		return;
	}

	const idleLabel = String(button.dataset.sdConfigIdleLabel || '').trim() || readButtonLabel(button) || 'Get Machine Advice';
	button.dataset.sdConfigIdleLabel = idleLabel;

	let label = idleLabel;
	let loading = false;

	if (state === 'loading') {
		label = getConfiguredLoadingLabel(app.config);
		loading = true;
	}

	writeButtonLabel(button, label);
	setButtonLoading(button, loading);

	const previousState = form.dataset.sdConfigBoundaryButtonState || '';
	if (previousState === state) {
		return;
	}

	form.dataset.sdConfigBoundaryButtonState = state;
	app.logger.debug(`ui.boundary_button.${state}`, {
		formId: app.config.formId,
		label,
		...context,
	});
}

function setAdviceLoading(form, loading) {
	form.dataset.sdConfigAdviceLoading = loading ? '1' : '0';
}

function isAdviceLoading(form) {
	return form.dataset.sdConfigAdviceLoading === '1';
}

function setAdviceNoResults(form, noResults) {
	form.dataset[ADVICE_NO_RESULTS_DATA_KEY] = noResults ? '1' : '0';
}

function clearAdviceNoResults(form) {
	form.dataset[ADVICE_NO_RESULTS_DATA_KEY] = '0';
}

function isAdviceNoResults(form) {
	return form.dataset[ADVICE_NO_RESULTS_DATA_KEY] === '1';
}

/**
* Responsibility:
* - Resolve current GF page number from shared app event state.
*
* Touches:
* - Reads `app.currentPage` written by modern GF render/page-change hooks.
*
* Guarantees:
* - Returns `0` when page state is unavailable.
* - Returns a positive integer page number when available.
*/
function readCurrentPageNumber(app) {
	const pageNumber = Number(app?.currentPage || 0);
	if (!Number.isFinite(pageNumber) || pageNumber <= 0) {
		return 0;
	}

	return Math.trunc(pageNumber);
}

/**
* Responsibility:
* - Persist the last successful advice payload signature on the form element.
*
* Touches:
* - Writes to `form.dataset[ADVICE_PAYLOAD_SIGNATURE_DATA_KEY]`.
*
* Guarantees:
* - Stores an empty string when signature is not usable.
*/
function writeAdvicePayloadSignature(form, signature) {
	form.dataset[ADVICE_PAYLOAD_SIGNATURE_DATA_KEY] = String(signature || '').trim();
}

/**
* Responsibility:
* - Read the last successful advice payload signature from form state.
*
* Touches:
* - Reads `form.dataset[ADVICE_PAYLOAD_SIGNATURE_DATA_KEY]`.
*
* Guarantees:
* - Returns a trimmed signature string or empty string.
*/
function readAdvicePayloadSignature(form) {
	return String(form.dataset[ADVICE_PAYLOAD_SIGNATURE_DATA_KEY] || '').trim();
}

/**
* Responsibility:
* - Persist the latest attempted advice payload signature to avoid auto-retry loops.
*
* Touches:
* - Writes to `form.dataset[ADVICE_LAST_ATTEMPT_SIGNATURE_DATA_KEY]`.
*
* Guarantees:
* - Stores an empty string when no attempt signature is available.
*/
function writeAdviceAttemptSignature(form, signature) {
	form.dataset[ADVICE_LAST_ATTEMPT_SIGNATURE_DATA_KEY] = String(signature || '').trim();
}

/**
* Responsibility:
* - Read the latest attempted advice payload signature from form state.
*
* Touches:
* - Reads `form.dataset[ADVICE_LAST_ATTEMPT_SIGNATURE_DATA_KEY]`.
*
* Guarantees:
* - Returns a trimmed signature string or empty string.
*/
function readAdviceAttemptSignature(form) {
	return String(form.dataset[ADVICE_LAST_ATTEMPT_SIGNATURE_DATA_KEY] || '').trim();
}

/**
* Responsibility:
* - Resolve numeric form id from a GF form element.
*
* Touches:
* - Reads `id` attribute and `data-formid` attribute.
*
* Guarantees:
* - Returns `0` when id cannot be resolved.
*/
function getFormIdFromElement(form) {
	if (!(form instanceof HTMLElement)) {
		return 0;
	}

	const dataFormId = Number.parseInt(String(form.dataset.formid || ''), 10);
	if (Number.isFinite(dataFormId) && dataFormId > 0) {
		return dataFormId;
	}

	const idMatch = String(form.id || '').match(/^gform_(\d+)$/);
	if (!idMatch) {
		return 0;
	}

	const parsed = Number.parseInt(idMatch[1], 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
* Responsibility:
* - Persist active abort controller for the current advice request of a form.
*
* Touches:
* - Writes to `adviceActiveAbortControllerByForm`.
*
* Guarantees:
* - Replaces any previous controller reference for the same form id.
*/
function writeActiveAdviceAbortController(formId, controller) {
	if (!(controller instanceof AbortController)) {
		return;
	}

	adviceActiveAbortControllerByForm.set(String(formId), controller);
}

/**
* Responsibility:
* - Clear active abort controller reference for a form.
*
* Touches:
* - Mutates `adviceActiveAbortControllerByForm`.
*
* Guarantees:
* - Removes the controller when the expected controller matches or when no expected value is provided.
*/
function clearActiveAdviceAbortController(formId, expectedController = null) {
	const key = String(formId);
	const currentController = adviceActiveAbortControllerByForm.get(key);
	if (!(currentController instanceof AbortController)) {
		adviceActiveAbortControllerByForm.delete(key);
		return;
	}

	if (expectedController instanceof AbortController && currentController !== expectedController) {
		return;
	}

	adviceActiveAbortControllerByForm.delete(key);
}

/**
* Responsibility:
* - Abort in-flight advice fetch for a form when superseded by new navigation/input state.
*
* Touches:
* - Calls `AbortController.abort()` on active controller and clears stored reference.
*
* Guarantees:
* - No-op when no active controller exists.
*/
function abortActiveAdviceRequest(formId) {
	const key = String(formId);
	const controller = adviceActiveAbortControllerByForm.get(key);
	if (!(controller instanceof AbortController)) {
		adviceActiveAbortControllerByForm.delete(key);
		return;
	}

	controller.abort();
	adviceActiveAbortControllerByForm.delete(key);
}

/**
* Responsibility:
* - Check whether advice container already has rendered result markup.
*
* Touches:
* - Reads `container.innerHTML`.
*
* Guarantees:
* - Returns true only when container exists and has non-empty HTML.
*/
function hasRenderedAdvice(container) {
	if (!(container instanceof HTMLElement)) {
		return false;
	}

	return String(container.innerHTML || '').trim() !== '';
}

/**
* Responsibility:
* - Derive no-results state from rendered advice markup markers.
*
* Touches:
* - Reads rendered advice result CSS class markers.
*
* Guarantees:
* - Returns `true`/`false` when marker is detectable.
* - Returns `null` when marker is absent.
*/
function readNoResultsFromContainer(container) {
	if (!(container instanceof HTMLElement)) {
		return null;
	}

	const resultNode = container.querySelector('.sd-configurator__result');
	if (resultNode instanceof HTMLElement) {
		return resultNode.classList.contains('sd-configurator__result__no-results');
	}

	return null;
}

/**
* Responsibility:
* - Synchronize form no-results state from rendered advice markup when cache is unavailable.
*
* Touches:
* - Reads container markers via `readNoResultsFromContainer()`.
* - Writes form dataset via `setAdviceNoResults()`.
*
* Guarantees:
* - Returns `true` when no-results state is detected and synchronized.
*/
function syncAdviceNoResultsFromContainer(form, container) {
	const detected = readNoResultsFromContainer(container);
	if (typeof detected !== 'boolean') {
		return false;
	}

	setAdviceNoResults(form, detected);
	return true;
}

/**
* Responsibility:
* - Rehydrate advice UI from cached markup when data is valid but DOM was re-rendered empty.
*
* Touches:
* - Reads cache via shared cache helper.
* - Writes result markup and no-results state back into form UI.
*
* Guarantees:
* - Returns true only when cache exists and rehydration succeeds.
*/
function rehydrateAdviceFromCache(app, form, container, signature) {
	const cached = readResultMarkupCache(adviceMarkupCacheByForm, app.config.formId, signature);
	if (!cached) {
		return false;
	}

	if (!setContainerMarkup(container, cached.markup)) {
		return false;
	}

	setAdviceNoResults(form, cached.noResults);
	syncAdviceStepNextState(form, app.config);

	return true;
}

function shouldDisableAdviceStepNext(form, config) {
	if (isAdviceLoading(form)) {
		return true;
	}

	if (!isAdviceReady(form, config)) {
		return true;
	}

	if (isAdviceNoResults(form)) {
		return true;
	}

	return false;
}

function syncAdviceStepNextState(form, config) {
	setAdviceStepNextDisabled(form, config, shouldDisableAdviceStepNext(form, config));
	dispatchStepStateChange(form, 'advice_state_sync');
}

function nextAdviceRequestToken(form) {
	const current = Number.parseInt(String(form.dataset[ADVICE_REQUEST_SEQ_DATA_KEY] || '0'), 10);
	const next = Number.isFinite(current) ? current + 1 : 1;
	const token = String(next);
	form.dataset[ADVICE_REQUEST_SEQ_DATA_KEY] = token;
	form.dataset[ADVICE_REQUEST_ACTIVE_DATA_KEY] = token;
	return token;
}

function invalidateAdviceRequest(form) {
	nextAdviceRequestToken(form);
}

function isAdviceRequestCurrent(form, token) {
	return String(form.dataset[ADVICE_REQUEST_ACTIVE_DATA_KEY] || '') === String(token);
}

function buildAdvicePayload(form, adviceInputMap) {
	const payload = {};
	const fieldErrors = {};

	for (const [apiKey, map] of Object.entries(adviceInputMap || {})) {
		const ids = Array.isArray(map.inputIds) ? map.inputIds : [];
		const valueType = map.valueType || 'string';
		const fieldType = String(map.fieldType || '').toLowerCase();
		const values = ids
			.map((inputId) => readInputValue(form, inputId))
			.filter((value) => value !== null);

		if (valueType === 'boolean') {
			if (fieldType === 'checkbox' && ids.length > 1) {
				let hasTrueValue = false;
				let hasInvalidValue = false;
				values.forEach((value) => {
					const parsed = toBoolean(value);
					if (parsed === null) {
						hasInvalidValue = true;
						return;
					}

					if (parsed === true) {
						hasTrueValue = true;
					}
				});
				payload[apiKey] = hasInvalidValue ? null : hasTrueValue;
				if (hasInvalidValue) {
					fieldErrors[apiKey] = `${apiKey} must be a boolean value.`;
				}
				continue;
			}

			const parsed = toBoolean(values[0]);
			payload[apiKey] = parsed;
			if (parsed === null) {
				fieldErrors[apiKey] = `${apiKey} must be a boolean value.`;
			}
			continue;
		}

		if (valueType === 'integer') {
			const parsed = toInteger(values[0]);
			payload[apiKey] = parsed;
			if (parsed === null) {
				fieldErrors[apiKey] = `${apiKey} must be an integer value.`;
			}
			continue;
		}

		if (valueType === 'number') {
			const parsed = toNumber(values[0]);
			payload[apiKey] = parsed;
			if (parsed === null) {
				fieldErrors[apiKey] = `${apiKey} must be a numeric value.`;
			}
			continue;
		}

		payload[apiKey] = values.length > 0 ? String(values[0] || '').trim() : '';
	}

	return {
		payload,
		fieldErrors,
	};
}

function getAdviceContainer(page, containerClass) {
	if (!page) {
		return null;
	}

	return page.querySelector(`.${containerClass}`);
}

function setContainerMarkup(container, markup) {
	if (!container) {
		return false;
	}

	const html = typeof markup === 'string' ? markup.trim() : '';
	// Contract: this HTML is trusted only when server-side renderers sanitize/escape output.
	container.innerHTML = html;

	return html !== '';
}

function renderLoading(container, config) {
	setContainerMarkup(container, config.adviceLoadingMarkup);
}

function renderStale(container, config) {
	setContainerMarkup(container, config.adviceStaleMarkup);
}

function renderNotReady(container, config) {
	setContainerMarkup(container, config.adviceNotReadyMarkup);
}

function renderAdvice(container, result) {
	return setContainerMarkup(container, result?.markup);
}

function renderError(container, config, message, onRetry) {
	if (!setContainerMarkup(container, config.adviceErrorMarkup)) {
		return;
	}

	if (typeof message === 'string' && message.trim() !== '') {
		const messageNode = container.querySelector('.sd-configurator__error-message-marker');
		if (messageNode) {
			messageNode.textContent = message;
		}
	}

	const retryButton = container.querySelector('.sd-config__advice-retry');
	if (retryButton instanceof HTMLButtonElement || retryButton instanceof HTMLInputElement) {
		retryButton.addEventListener('click', () => {
			onRetry();
		}, { once: true });
	}
}

function readHiddenValue(form, hiddenFieldMap, key) {
	const fieldId = hiddenFieldMap?.[key];
	if (!fieldId) {
		return '';
	}

	const value = readInputValue(form, fieldId);
	if (Array.isArray(value)) {
		return value[0] || '';
	}

	return value || '';
}

function writeHiddenValue(form, hiddenFieldMap, key, value) {
	const fieldId = hiddenFieldMap?.[key];
	if (!fieldId) {
		return;
	}

	const names = [
		`input_${fieldId}`,
		`input_${String(fieldId).replace('.', '_')}`,
	];

	for (const name of names) {
		const elements = getElementsByInputName(form, name);
		if (elements.length === 0) {
			continue;
		}

		elements.forEach((element) => {
			if ('value' in element) {
				element.value = value == null ? '' : String(value);
			}
		});
		return;
	}
}

function setAdviceState(form, config, payload) {
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_ctx', payload.ctx || '');
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_status', payload.status || '');
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_hash', payload.hash || '');
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_fetched_at', payload.fetchedAt || '');
}

function clearAdviceState(form, config, status = '') {
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_ctx', '');
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_status', status);
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_hash', '');
	writeHiddenValue(form, config.hiddenFieldMap, 'sd_advice_fetched_at', '');
}

function isAdviceReady(form, config) {
	const ctx = readHiddenValue(form, config.hiddenFieldMap, 'sd_advice_ctx');
	const status = readHiddenValue(form, config.hiddenFieldMap, 'sd_advice_status');
	const hash = readHiddenValue(form, config.hiddenFieldMap, 'sd_advice_hash');

	return ctx !== '' && hash !== '' && status === config.statuses.success;
}

function buildTrackedNames(adviceInputMap) {
	const names = new Set();

	Object.values(adviceInputMap || {}).forEach((map) => {
		const ids = Array.isArray(map.inputIds) ? map.inputIds : [];
		ids.forEach((inputId) => {
			const baseNames = [
				`input_${inputId}`,
				`input_${String(inputId).replace('.', '_')}`,
			];

			baseNames.forEach((baseName) => {
				names.add(baseName);
				names.add(`${baseName}[]`);
			});
		});
	});

	return names;
}

function isTransientError(error) {
	if (error?.transient === true) {
		return true;
	}

	const status = Number(error?.status || 0);
	if (status === 0 || status === 429) {
		return true;
	}

	return status >= 500;
}

function validateHiddenFieldMap(config) {
	const missing = REQUIRED_HIDDEN_KEYS.filter((key) => {
		const fieldId = config.hiddenFieldMap?.[key];
		return typeof fieldId !== 'string' || fieldId.trim() === '';
	});

	return missing;
}

/**
* Responsibility:
* - Resolve precise diagnostics for machine-advice request failures.
*
* Touches:
* - Consumes `AdviceRequestError` metadata when available and falls back to status-based inference otherwise.
*
* Guarantees:
* - Returns stable failure details for console logging without discarding server-provided context.
*/
function resolveAdviceRequestFailureDetails(error) {
	const whatWentWrong = String(error?.whatWentWrong || '').trim();
	const likelyCause = String(error?.likelyCause || '').trim();
	const howToFix = String(error?.howToFix || '').trim();
	if (whatWentWrong !== '' || likelyCause !== '' || howToFix !== '') {
		return {
			failureCode: String(error?.failureCode || '').trim(),
			failureStage: String(error?.failureStage || '').trim(),
			providerHttpCode: Number(error?.providerHttpCode || 0),
			providerMessage: String(error?.providerMessage || '').trim(),
			whatWentWrong,
			likelyCause,
			howToFix,
		};
	}

	const status = Number(error?.status || 0);

	return {
		failureCode: String(error?.failureCode || '').trim(),
		failureStage: String(error?.failureStage || '').trim(),
		providerHttpCode: Number(error?.providerHttpCode || 0),
		providerMessage: String(error?.providerMessage || '').trim(),
		whatWentWrong: status === 422
			? 'Advice result endpoint rejected the request as invalid input.'
			: 'Result request did not complete with a successful advice payload.',
		likelyCause: status === 401 || status === 403
			? 'Security validation failed (missing/expired nonce or blocked origin).'
			: status === 404
				? 'Result endpoint was not found.'
				: status === 422
					? 'The request reached the backend, but either the request payload or the upstream advice payload was invalid.'
					: status === 429
						? 'Result route or upstream API is rate-limiting requests.'
						: status >= 500
							? 'Server-side result or upstream API failed while generating advice.'
							: 'Request failed before a successful result response was returned.',
		howToFix: status === 401 || status === 403
			? 'Reload the page for a fresh nonce and verify REST nonce/header checks and allowed origin.'
			: status === 404
				? 'Verify the result REST route is registered and `adviceResultEndpoint` points to the correct URL.'
				: status === 422
					? 'Inspect the REST response body and traceId-backed plugin logs to see whether the failure came from request serialization or upstream input validation.'
					: status === 429
						? 'Retry after cooldown and check API/provider rate-limit settings.'
						: status >= 500
							? 'Review server logs and upstream API logs using the traceId to identify backend failures.'
							: 'Check browser Network tab, CORS, and site connectivity.',
	};
}

async function requestResult(config, payload, logger, controller = null) {
	const timeoutMs = resolveRequestTimeoutMs(config);
	let timeoutId = 0;
	if (controller && timeoutMs > 0) {
		timeoutId = window.setTimeout(() => {
			controller.abort();
		}, timeoutMs);
	}

	let response;
	try {
		response = await fetch(config.adviceResultEndpoint, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'X-SD-Config-Nonce': config.adviceResultNonce,
				'X-WP-Nonce': config.adviceResultNonce,
			},
			body: JSON.stringify({
				formId: config.formId,
				_wpnonce: config.adviceResultNonce,
				payload,
			}),
			signal: controller ? controller.signal : undefined,
		});
	} catch (error) {
		if (timeoutId > 0) {
			window.clearTimeout(timeoutId);
		}

		const aborted = controller ? controller.signal.aborted === true : error?.name === 'AbortError';
		throw new AdviceRequestError(config.adviceErrorText || '', {
			status: 0,
			transient: true,
			traceId: '',
			retryAfterMs: aborted ? timeoutMs : 0,
			failureCode: aborted ? 'request_aborted' : 'request_transport_error',
			failureStage: aborted ? 'browser_request_timeout' : 'browser_request_transport',
			whatWentWrong: aborted
				? 'Machine advice request was aborted before a response was received.'
				: 'Browser could not complete the request to the advice result endpoint.',
			likelyCause: aborted
				? 'The request timed out or a newer advice request replaced the current one.'
				: 'Network, DNS, CORS, or site connectivity prevented the request from completing.',
			howToFix: aborted
				? 'Retry the request and check whether repeated page/input transitions are cancelling the request.'
				: 'Check the browser Network tab and site connectivity, then retry the request.',
			providerHttpCode: 0,
			providerMessage: '',
		});
	}

	if (timeoutId > 0) {
		window.clearTimeout(timeoutId);
	}

	let body = {};
	try {
		body = await response.json();
	} catch (error) {
		logger.error('Machine advice result failed: API returned a non-JSON response.', {
			status: response.status,
			endpoint: config.adviceResultEndpoint,
			whatWentWrong: 'Response body could not be parsed as JSON.',
			likelyCause: 'Endpoint returned HTML/error output (for example a PHP warning, proxy page, or auth redirect).',
			howToFix: 'Inspect the result request response body in browser Network tab and make sure the route returns valid JSON.',
		});

		throw new AdviceRequestError(config.adviceErrorText || '', {
			status: response.status,
			transient: response.status === 0 || response.status === 429 || response.status >= 500,
			traceId: response.headers.get('X-SD-Trace-Id') || '',
			retryAfterMs: parseRetryAfterMs(response),
			failureCode: 'rest_response_not_json',
			failureStage: 'rest_response_decode',
			whatWentWrong: 'Response body could not be parsed as JSON.',
			likelyCause: 'Endpoint returned HTML/error output (for example a PHP warning, proxy page, or auth redirect).',
			howToFix: 'Inspect the result request response body in browser Network tab and make sure the route returns valid JSON.',
			providerHttpCode: 0,
			providerMessage: '',
		});
	}

	if (!response.ok || body.success !== true) {
		throw new AdviceRequestError(body?.message || config.adviceErrorText || '', {
			status: response.status,
			transient: body?.transient === true || response.status === 429 || response.status >= 500,
			traceId: body?.traceId || response.headers.get('X-SD-Trace-Id') || '',
			retryAfterMs: parseRetryAfterMs(response),
			failureCode: body?.failureCode || '',
			failureStage: body?.failureStage || '',
			whatWentWrong: body?.whatWentWrong || '',
			likelyCause: body?.likelyCause || '',
			howToFix: body?.howToFix || '',
			providerHttpCode: body?.providerHttpCode || 0,
			providerMessage: body?.providerMessage || '',
		});
	}

	return body;
}

async function resultAtAdviceStep({
	app,
	form,
	page,
	autoRetry = true,
	payloadOverride = null,
	payloadFieldErrorsOverride = null,
	payloadSignatureOverride = '',
}) {
	if (isAdviceLoading(form)) {
		return;
	}
	clearAdviceNoResults(form);
	setAdviceStepNextDisabled(form, app.config, true);

	const container = getAdviceContainer(page, app.config.adviceContainerClass);
	if (!container) {
		setBoundaryButtonState(app, form, 'error', {
			reason: 'container_missing',
		});
		app.logger.error('Machine advice cannot be shown: output container is missing on the dedicated advice step.', {
			formId: app.config.formId,
			containerClass: app.config.adviceContainerClass,
			boundaryClass: app.config.boundaryClass,
			currentPage: getPageNumber(page),
			whatWentWrong: 'No element matching the configured output class was found on the advice page.',
			likelyCause: 'The HTML field with the advice container class is missing or placed on a different page.',
			howToFix: `Place <div class="${app.config.adviceContainerClass}"></div> on the dedicated advice step right after the "Get Machine Advice" boundary page break.`,
		});
		return;
	}

	const payloadResult = payloadOverride && typeof payloadOverride === 'object'
		? {
			payload: payloadOverride,
			fieldErrors: payloadFieldErrorsOverride && typeof payloadFieldErrorsOverride === 'object'
				? payloadFieldErrorsOverride
				: {},
		}
		: buildAdvicePayload(form, app.config.adviceInputMap);
	const payload = payloadResult.payload;
	const payloadFieldErrors = payloadResult.fieldErrors || {};
	const payloadSignature = String(payloadSignatureOverride || '').trim() || buildPayloadSignature(payload);
	writeAdviceAttemptSignature(form, payloadSignature);

	if (Object.keys(payloadFieldErrors).length > 0) {
		const validationMessage = String(Object.values(payloadFieldErrors)[0] || 'One or more advice inputs are invalid.').trim();
		writeAdvicePayloadSignature(form, '');
		clearAdviceState(form, app.config, app.config.statuses.error);
		syncAdviceStepNextState(form, app.config);
		renderError(container, app.config, validationMessage, () => {
			resultAtAdviceStep({
				app,
				form,
				page,
				autoRetry: false,
			});
		});
		setBoundaryButtonState(app, form, 'error', {
			status: 422,
			reason: 'payload_validation',
		});
		return;
	}

	const formId = Number(app.config.formId || 0);
	abortActiveAdviceRequest(formId);
	const controller = typeof AbortController === 'function' ? new AbortController() : null;
	if (controller) {
		writeActiveAdviceAbortController(formId, controller);
	}

	setAdviceLoading(form, true);
	syncAdviceStepNextState(form, app.config);
	setMachineAdviceActiveState(form, true);
	setBoundaryButtonState(app, form, 'loading', {
		page: getPageNumber(page),
	});
	renderLoading(container, app.config);
	const requestToken = nextAdviceRequestToken(form);
	const minimumLoadingDelay = waitForMinimumLoadingDelay(app.config);
	const mappedAdviceKeys = Object.keys(app.config.adviceInputMap || {});
	const expectedAdviceKeys = Array.isArray(app.config.expectedAdviceKeys)
		? app.config.expectedAdviceKeys
		: [];
	const missingAdviceKeys = expectedAdviceKeys.filter((key) => !mappedAdviceKeys.includes(key));
	app.logger.debug('Advice result request on dedicated advice step.', {
		formId: app.config.formId,
		mappedAdviceKeys,
		missingAdviceKeys,
		payload,
	});

	try {
		const result = await requestResult(app.config, payload, app.logger, controller);
		await minimumLoadingDelay;
		if (!isAdviceRequestCurrent(form, requestToken)) {
			clearActiveAdviceAbortController(formId, controller);
			return;
		}

		const rendered = renderAdvice(container, result);
		if (!rendered) {
			app.logger.error('Machine advice result succeeded but returned no markup to display.', {
				formId: app.config.formId,
				traceId: result?.traceId || '',
				whatWentWrong: 'Result response was successful, but `markup` was empty.',
				likelyCause: 'Renderer returned no HTML for this payload or response shape changed.',
				howToFix: 'Check server-side result response and ensure `markup` is returned as a non-empty string.',
			});
		}
		setAdviceState(form, app.config, {
			ctx: result.ctx,
			status: result.status || app.config.statuses.success,
			hash: result.hash,
			fetchedAt: result.fetchedAt,
		});
		writeAdvicePayloadSignature(form, payloadSignature);
		const machinesCount = Array.isArray(result?.machines) ? result.machines.length : 0;
		const noResults = machinesCount === 0;
		if (rendered) {
			writeResultMarkupCache(
				adviceMarkupCacheByForm,
				app.config.formId,
				payloadSignature,
				result?.markup,
				noResults
			);
		}
		setAdviceNoResults(form, noResults);
		syncAdviceStepNextState(form, app.config);

		app.logger.debug('Advice result success on dedicated advice step.', {
			formId: app.config.formId,
			traceId: result?.traceId || '',
			machinesCount,
			noResults,
		});
		setBoundaryButtonState(app, form, 'ready', {
			traceId: result?.traceId || '',
		});
	} catch (error) {
		await minimumLoadingDelay;
		if (!isAdviceRequestCurrent(form, requestToken)) {
			clearActiveAdviceAbortController(formId, controller);
			return;
		}

		const diagnostics = resolveAdviceRequestFailureDetails(error);

		app.logger.error('Machine advice result request failed.', {
			status: error?.status,
			message: error?.message,
			transient: error?.transient,
			traceId: error?.traceId || '',
			failureCode: diagnostics.failureCode,
			failureStage: diagnostics.failureStage,
			providerHttpCode: diagnostics.providerHttpCode,
			providerMessage: diagnostics.providerMessage,
			whatWentWrong: diagnostics.whatWentWrong,
			likelyCause: diagnostics.likelyCause,
			howToFix: diagnostics.howToFix,
		});

		if (autoRetry && isTransientError(error)) {
			setBoundaryButtonState(app, form, 'error', {
				reason: 'transient_retry',
			});
			const retryDelayMs = resolveRetryDelayMs(error, 1);
			await sleepMs(retryDelayMs);
			if (!isAdviceRequestCurrent(form, requestToken)) {
				return;
			}
			setAdviceLoading(form, false);
			setMachineAdviceActiveState(form, false);
			await resultAtAdviceStep({
				app,
				form,
				page,
				autoRetry: false,
			});
			return;
		}

		clearAdviceNoResults(form);
		writeAdvicePayloadSignature(form, '');
		clearAdviceState(form, app.config, app.config.statuses.error);
		syncAdviceStepNextState(form, app.config);
		renderError(container, app.config, error?.message, () => {
			resultAtAdviceStep({
				app,
				form,
				page,
				autoRetry: false,
			});
		});
		setBoundaryButtonState(app, form, 'error', {
			status: error?.status,
		});
	} finally {
		clearActiveAdviceAbortController(formId, controller);
		if (!isAdviceRequestCurrent(form, requestToken)) {
			return;
		}

		form.dataset[ADVICE_REQUEST_ACTIVE_DATA_KEY] = '';
		setAdviceLoading(form, false);
		syncAdviceStepNextState(form, app.config);
		setMachineAdviceActiveState(form, false);
	}
}

/**
* Responsibility:
* - Decide whether advice flow should fetch, skip, or rehydrate for current payload/page state.
*
* Touches:
* - Consumes signature/status/markup-presence inputs only (pure decision helper).
*
* Guarantees:
* - Returns deterministic action/reason tuple for flow orchestration.
*/
function getAdviceFetchDecision({
	force,
	adviceReady,
	status,
	errorStatus,
	currentPayloadSignature,
	successfulPayloadSignature,
	attemptedPayloadSignature,
	hasRenderedMarkup,
	hasCachedMarkup,
}) {
	if (force) {
		return {
			action: 'fetch',
			reason: 'forced',
		};
	}

	const sameSuccessfulSignature = adviceReady
		&& successfulPayloadSignature !== ''
		&& currentPayloadSignature !== ''
		&& successfulPayloadSignature === currentPayloadSignature;
	if (sameSuccessfulSignature) {
		if (hasRenderedMarkup) {
			return {
				action: 'skip',
				reason: 'reuse_rendered',
			};
		}

		if (hasCachedMarkup) {
			return {
				action: 'rehydrate',
				reason: 'reuse_cached_markup',
			};
		}

		return {
			action: 'fetch',
			reason: 'success_without_rendered_markup',
		};
	}

	const sameFailedSignature = status === errorStatus
		&& attemptedPayloadSignature !== ''
		&& currentPayloadSignature !== ''
		&& attemptedPayloadSignature === currentPayloadSignature;
	if (sameFailedSignature) {
		if (hasRenderedMarkup) {
			return {
				action: 'skip',
				reason: 'reuse_rendered_error',
			};
		}

		if (hasCachedMarkup) {
			return {
				action: 'rehydrate',
				reason: 'reuse_cached_error_markup',
			};
		}

		return {
			action: 'fetch',
			reason: 'error_without_rendered_markup',
		};
	}

	return {
		action: 'fetch',
		reason: 'fetch_needed',
	};
}

/**
* Responsibility:
* - Ensure machine advice exists for the currently active GF page.
*
* Touches:
* - Reads current-page state from GF hook events (`app.currentPage`).
* - Runs decision logic and executes skip/rehydrate/fetch behavior.
*
* Guarantees:
* - Fetches only when active page is the advice page and decision says fetch.
* - Rehydrates cached markup when reusable success state exists but container is empty.
*/
function ensureAdviceResultForCurrentPage(app, form, { force = false, reason = '', currentPage = 0 } = {}) {
	const advicePage = getAdvicePage(form, app.config.adviceContainerClass);
	if (!advicePage) {
		return;
	}
	const advicePageNumber = getPageNumber(advicePage);
	const currentPageNumber = Number.isFinite(Number(currentPage)) && Number(currentPage) > 0
		? Math.trunc(Number(currentPage))
		: readCurrentPageNumber(app);
	if (currentPageNumber <= 0 || currentPageNumber !== advicePageNumber) {
		return;
	}

	if (isAdviceLoading(form)) {
		return;
	}

	const payloadResult = buildAdvicePayload(form, app.config.adviceInputMap);
	const payload = payloadResult.payload;
	const currentPayloadSignature = buildPayloadSignature(payload);
	const successfulPayloadSignature = readAdvicePayloadSignature(form);
	const attemptedPayloadSignature = readAdviceAttemptSignature(form);
	const status = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_advice_status');
	const adviceReady = isAdviceReady(form, app.config);
	const container = getAdviceContainer(advicePage, app.config.adviceContainerClass);
	const cached = readResultMarkupCache(adviceMarkupCacheByForm, app.config.formId, currentPayloadSignature);
	const decision = getAdviceFetchDecision({
		force,
		adviceReady,
		status,
		errorStatus: app.config.statuses.error,
		currentPayloadSignature,
		successfulPayloadSignature,
		attemptedPayloadSignature,
		hasRenderedMarkup: hasRenderedAdvice(container),
		hasCachedMarkup: cached !== null,
	});

	if (decision.action === 'skip') {
		if (cached) {
			setAdviceNoResults(form, cached.noResults);
			syncAdviceStepNextState(form, app.config);
		} else if (syncAdviceNoResultsFromContainer(form, container)) {
			syncAdviceStepNextState(form, app.config);
		}
		return;
	}

	if (decision.action === 'rehydrate') {
		if (rehydrateAdviceFromCache(app, form, container, currentPayloadSignature)) {
			setBoundaryButtonState(app, form, status === app.config.statuses.error ? 'error' : 'ready', {
				reason: 'rehydrated_cached_markup',
			});
			return;
		}
	}

	app.logger.debug('Starting machine advice result on dedicated advice step.', {
		formId: app.config.formId,
		reason,
		decision: decision.reason,
		status,
		currentPage: currentPageNumber,
		advicePage: getPageNumber(advicePage),
		currentPayloadSignature,
		successfulPayloadSignature,
		attemptedPayloadSignature,
	});

	resultAtAdviceStep({
		app,
		form,
		page: advicePage,
		autoRetry: true,
		payloadOverride: payload,
		payloadFieldErrorsOverride: payloadResult.fieldErrors,
		payloadSignatureOverride: currentPayloadSignature,
	});
}

function invalidateAdviceIfNeeded(app, form, eventTarget, trackedNames) {
	if (!(eventTarget instanceof Element)) {
		return;
	}

	const inputName = eventTarget.getAttribute('name') || '';
	if (!trackedNames.has(inputName)) {
		return;
	}

	const currentPayloadSignature = buildPayloadSignature(buildAdvicePayload(form, app.config.adviceInputMap).payload);
	const successfulPayloadSignature = readAdvicePayloadSignature(form);
	const currentCtx = readHiddenValue(form, app.config.hiddenFieldMap, 'sd_advice_ctx');
	const loading = isAdviceLoading(form);
	const adviceReady = isAdviceReady(form, app.config);
	if (
		!loading
		&& adviceReady
		&& successfulPayloadSignature !== ''
		&& currentPayloadSignature !== ''
		&& successfulPayloadSignature === currentPayloadSignature
	) {
		return;
	}

	if (!currentCtx && !loading) {
		return;
	}

	abortActiveAdviceRequest(app.config.formId);

	if (loading) {
		invalidateAdviceRequest(form);
		setAdviceLoading(form, false);
		setMachineAdviceActiveState(form, false);
	}

	clearAdviceNoResults(form);
	clearResultMarkupCache(adviceMarkupCacheByForm, app.config.formId);
	writeAdvicePayloadSignature(form, '');
	writeAdviceAttemptSignature(form, '');
	clearAdviceState(form, app.config, app.config.statuses.stale);
	writeHiddenValue(form, app.config.hiddenFieldMap, 'sd_roi_status', app.config.statuses.stale);
	syncAdviceStepNextState(form, app.config);
	setBoundaryButtonState(app, form, 'idle', {
		reason: 'input_changed',
		inputName,
	});

	app.logger.debug('Advice state invalidated after part 1 change.', {
		inputName,
	});
}

function syncBoundaryUiState(app, form) {
	if (isAdviceReady(form, app.config)) {
		if (readAdvicePayloadSignature(form) === '') {
			const payload = buildAdvicePayload(form, app.config.adviceInputMap).payload;
			writeAdvicePayloadSignature(form, buildPayloadSignature(payload));
		}
		const cached = readResultMarkupCache(
			adviceMarkupCacheByForm,
			app.config.formId,
			readAdvicePayloadSignature(form)
		);
		let restoredFromCacheOrDom = false;
		if (cached) {
			setAdviceNoResults(form, cached.noResults);
			restoredFromCacheOrDom = true;
		} else {
			const container = getAdviceStepContainer(form, app.config);
			restoredFromCacheOrDom = syncAdviceNoResultsFromContainer(form, container);
		}
		if (!restoredFromCacheOrDom) {
			clearAdviceNoResults(form);
		}
		setBoundaryButtonState(app, form, 'ready', {
			reason: 'sync_ready',
		});
	} else {
		setBoundaryButtonState(app, form, 'idle', {
			reason: 'sync_idle',
		});
	}
	syncAdviceStepNextState(form, app.config);

	ensureAdviceResultForCurrentPage(app, form, {
		force: false,
		reason: 'sync',
	});
}

function validateDedicatedAdviceStepLayout(app, form) {
	const boundaryPages = findBoundaryPageNumbers(form, app.config.boundaryClass);
	if (boundaryPages.length === 0) {
		app.logger.error('Machine advice trigger is not configured: boundary class was not found in this form.', {
			formId: app.config.formId,
			boundaryClass: app.config.boundaryClass,
			whatWentWrong: 'No page contains the configured machine-advice boundary class.',
			likelyCause: 'Boundary class is missing or misspelled on the intended page break/page container.',
			howToFix: `Place "${app.config.boundaryClass}" on the page where clicking Next should trigger machine advice.`,
		});
		return false;
	}

	if (boundaryPages.length !== 1) {
		app.logger.error('Machine advice boundary configuration is invalid: expected exactly one boundary page.', {
			formId: app.config.formId,
			boundaryClass: app.config.boundaryClass,
			boundaryPages,
			whatWentWrong: 'Multiple pages are marked as machine-advice boundary.',
			likelyCause: 'Boundary class was applied to more than one page break or page section.',
			howToFix: `Use "${app.config.boundaryClass}" on exactly one page break (the end of Part 1).`,
		});
		return false;
	}

	const advicePages = findAdvicePages(form, app.config.adviceContainerClass)
		.map((page) => getPageNumber(page))
		.filter((pageNumber) => pageNumber > 0);

	if (advicePages.length === 0) {
		app.logger.error('Machine advice output is not configured: dedicated advice step container was not found.', {
			formId: app.config.formId,
			containerClass: app.config.adviceContainerClass,
			whatWentWrong: 'No page contains the configured machine-advice output container.',
			likelyCause: 'The HTML field with the advice container class is missing.',
			howToFix: `Add an HTML field with <div class="${app.config.adviceContainerClass}"></div> on the dedicated advice step.`,
		});
		return false;
	}

	if (advicePages.length !== 1) {
		app.logger.error('Machine advice output configuration is invalid: expected exactly one advice step container page.', {
			formId: app.config.formId,
			containerClass: app.config.adviceContainerClass,
			advicePages,
			whatWentWrong: 'Multiple pages contain the configured advice output class.',
			likelyCause: 'Advice output container class was used more than once.',
			howToFix: `Use "${app.config.adviceContainerClass}" on exactly one dedicated advice step.`,
		});
		return false;
	}

	const boundaryPage = boundaryPages[0];
	const advicePage = advicePages[0];

	if (advicePage <= boundaryPage) {
		app.logger.error('Machine advice flow order is invalid: advice step must come after boundary step.', {
			formId: app.config.formId,
			boundaryPage,
			advicePage,
			whatWentWrong: 'Dedicated advice output is on or before the boundary page.',
			likelyCause: 'Advice container is still placed in the old layout location.',
			howToFix: 'Move the advice output container to a later page than the boundary step.',
		});
		return false;
	}

	return true;
}

/**
* Responsibility:
* - Read source page number for the current GF submission request.
*
* Touches:
* - Reads `gform_source_page_number_{formId}` hidden input from the form.
*
* Guarantees:
* - Returns `0` when source page cannot be determined.
*/
function readSubmissionSourcePage(form, formId) {
	if (!(form instanceof HTMLElement)) {
		return 0;
	}

	const sourceInput = form.querySelector(`[name="gform_source_page_number_${formId}"]`);
	if (!(sourceInput instanceof HTMLInputElement || sourceInput instanceof HTMLTextAreaElement || sourceInput instanceof HTMLSelectElement)) {
		return 0;
	}

	const parsed = Number.parseInt(String(sourceInput.value || ''), 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
* Responsibility:
* - Detect whether GF submission payload represents a previous-page action.
*
* Touches:
* - Reads `data.submissionType` and GF submission constants when available.
*
* Guarantees:
* - Returns `true` only for previous navigation requests.
*/
function isPreviousSubmissionType(data) {
	const submissionType = String(data?.submissionType || '').trim().toLowerCase();
	const previousConstant = String(window?.gform?.submission?.SUBMISSION_TYPE_PREVIOUS || 'previous').trim().toLowerCase();

	return submissionType !== '' && (submissionType === previousConstant || submissionType === 'previous');
}

/**
* Responsibility:
* - Detect whether GF submission payload represents a next-page action.
*
* Touches:
* - Reads `data.submissionType` and GF submission constants when available.
*
* Guarantees:
* - Returns `true` only for next navigation requests.
*/
function isNextSubmissionType(data) {
	const submissionType = String(data?.submissionType || '').trim().toLowerCase();
	const nextConstant = String(window?.gform?.submission?.SUBMISSION_TYPE_NEXT || 'next').trim().toLowerCase();

	return submissionType !== '' && (submissionType === nextConstant || submissionType === 'next');
}

/**
* Responsibility:
* - Register native GF pre-submission gate for advice-step transition control.
*
* Touches:
* - Registers one async filter on `gform/submission/pre_submission` per form id.
* - Aborts next-page submission when advice is loading, missing, or no-results.
*
* Guarantees:
* - Gate is registered at most once per form id.
* - Fetch trigger for not-ready state is executed via `ensureAdviceResultForCurrentPage(..., { force: true })`.
*/
function registerAdvicePreSubmissionFilter(app, advicePageNumber) {
	const formId = Number(app.config.formId || 0);
	if (!Number.isFinite(formId) || formId <= 0 || advicePageNumber <= 0) {
		return;
	}

	const registrationKey = String(formId);
	if (registeredPreSubmissionFilterForms.has(registrationKey)) {
		return;
	}

	const addAsyncFilter = window?.gform?.utils?.addAsyncFilter;
	if (typeof addAsyncFilter !== 'function') {
		app.logger.error('GF pre-submission hook unavailable: cannot register native advice transition gate.', {
			formId,
			whatWentWrong: 'gform.utils.addAsyncFilter is not available in this runtime.',
			likelyCause: 'GF frontend script utilities did not initialize before configurator binding.',
			howToFix: 'Ensure GF scripts are loaded and bind configurator after `gform/theme/scripts_loaded`.',
		});
		return;
	}

	addAsyncFilter('gform/submission/pre_submission', async (data) => {
		const targetForm = data?.form;
		if (!(targetForm instanceof HTMLElement)) {
			return data;
		}

		const submittedFormId = getFormIdFromElement(targetForm);
		if (submittedFormId !== formId) {
			return data;
		}

		if (isPreviousSubmissionType(data)) {
			return data;
		}

		if (!isNextSubmissionType(data)) {
			return data;
		}

		const sourcePage = readSubmissionSourcePage(targetForm, formId) || readCurrentPageNumber(app);
		if (sourcePage <= 0 || sourcePage !== advicePageNumber) {
			return data;
		}

		if (isAdviceLoading(targetForm)) {
			data.abort = true;
			setAdviceStepNextDisabled(targetForm, app.config, true);
			return data;
		}

		const adviceReady = isAdviceReady(targetForm, app.config);
		const container = getAdviceStepContainer(targetForm, app.config);
		if (!isAdviceNoResults(targetForm)) {
			syncAdviceNoResultsFromContainer(targetForm, container);
		}
		const noResults = isAdviceNoResults(targetForm);
		if (adviceReady && !noResults) {
			setAdviceStepNextDisabled(targetForm, app.config, false);
			return data;
		}

		data.abort = true;
		setAdviceStepNextDisabled(targetForm, app.config, true);

		if (adviceReady && noResults) {
			return data;
		}

		ensureAdviceResultForCurrentPage(app, targetForm, {
			force: true,
			reason: 'pre_submission_not_ready',
			currentPage: sourcePage,
		});

		if (container && !isAdviceLoading(targetForm) && !hasRenderedAdvice(container)) {
			renderNotReady(container, app.config);
		}

		return data;
	});

	registeredPreSubmissionFilterForms.add(registrationKey);
}

export function bindBoundaryAdviceFetch(app) {
	const form = getForm(app.config);
	if (!form) {
		app.logger.error('Machine advice cannot initialize: target Gravity Form is not present in the DOM.', {
			formId: app.config.formId,
			whatWentWrong: `Form container #gform_${app.config.formId} was not found.`,
			likelyCause: 'Wrong form ID in runtime config or script loaded before form markup exists on this page.',
			howToFix: 'Render the correct Gravity Forms form on this page and confirm runtime config uses the same form ID.',
		});
		return;
	}

	const missingHiddenFields = validateHiddenFieldMap(app.config);
	if (missingHiddenFields.length > 0) {
		app.logger.error('Machine advice state mapping is incomplete: required hidden fields are missing.', {
			formId: app.config.formId,
			missingHiddenFields,
			whatWentWrong: 'One or more hidden state fields could not be mapped by runtime config.',
			likelyCause: 'Required hidden fields do not exist on the form or their mapping names/labels do not match expected keys.',
			howToFix: 'Add hidden fields for sd_advice_ctx, sd_advice_status, sd_advice_hash, sd_advice_fetched_at and verify mappings in runtime config.',
		});
		return;
	}

	if (!validateDedicatedAdviceStepLayout(app, form)) {
		return;
	}

	setMachineAdviceActiveState(form, false);
	syncBoundaryUiState(app, form);

	if (form.dataset.sdConfigBoundaryBound === '1') {
		return;
	}

	const trackedNames = buildTrackedNames(app.config.adviceInputMap);
	const mappedAdviceKeys = Object.keys(app.config.adviceInputMap || {});
	const expectedAdviceKeys = Array.isArray(app.config.expectedAdviceKeys)
		? app.config.expectedAdviceKeys
		: [];
	const missingAdviceKeys = expectedAdviceKeys.filter((key) => !mappedAdviceKeys.includes(key));
	const boundaryPageNumber = getPageNumber(getBoundaryPage(form, app.config.boundaryClass));
	const advicePageNumber = getPageNumber(getAdvicePage(form, app.config.adviceContainerClass));
	registerAdvicePreSubmissionFilter(app, advicePageNumber);

	if (missingAdviceKeys.length > 0) {
		app.logger.error('Machine advice input mapping is incomplete: some API payload keys have no source field.', {
			formId: app.config.formId,
			mappedAdviceKeys,
			missingAdviceKeys,
			whatWentWrong: `Expected ${expectedAdviceKeys.length} mapped keys, found ${mappedAdviceKeys.length}.`,
			likelyCause: 'One or more fields are missing dynamic populate parameter names or checkbox choice mapping.',
			howToFix: 'Add/verify mapping for each missing key listed in `missingAdviceKeys`, then reload the form.',
		});
	}

	form.addEventListener(
		'click',
		(event) => {
			const target = event.target;
			if (!(target instanceof Element)) {
				return;
			}

			const previousButton = target.closest('.gform_previous_button');
			if (previousButton instanceof HTMLButtonElement || previousButton instanceof HTMLInputElement) {
				abortActiveAdviceRequest(app.config.formId);
				if (isAdviceLoading(form)) {
					invalidateAdviceRequest(form);
					setAdviceLoading(form, false);
				}
				setMachineAdviceActiveState(form, false);
				setBoundaryButtonState(app, form, 'idle', {
					reason: 'previous_clicked',
				});
				syncAdviceStepNextState(form, app.config);
				return;
			}

			const button = target.closest('.gform_next_button');
			if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) {
				return;
			}

			setMachineAdviceActiveState(form, false);
			const currentPageNumber = getPageNumber(button.closest('.gform_page'));

			if (advicePageNumber > 0 && currentPageNumber === advicePageNumber) {
				if (isAdviceLoading(form)) {
					setAdviceStepNextDisabled(form, app.config, true);
					app.logger.debug('Advice step next blocked while machine advice is loading.', {
						formId: app.config.formId,
					});
					return;
				}

				const container = getAdviceStepContainer(form, app.config);
				if (!isAdviceNoResults(form)) {
					syncAdviceNoResultsFromContainer(form, container);
				}
				const adviceReady = isAdviceReady(form, app.config);
				const noResults = isAdviceNoResults(form);
				if (adviceReady && !noResults) {
					setAdviceStepNextDisabled(form, app.config, false);
					return;
				}

				if (adviceReady && noResults) {
					setAdviceStepNextDisabled(form, app.config, true);
					app.logger.debug('Advice step next blocked because result returned no machine results.', {
						formId: app.config.formId,
					});
					return;
				}

				if (container && !isAdviceLoading(form)) {
					renderNotReady(container, app.config);
				}
				return;
			}

			if (boundaryPageNumber > 0 && currentPageNumber === boundaryPageNumber) {
				clearAdviceNoResults(form);
				setBoundaryButtonState(app, form, 'loading', {
					reason: 'boundary_clicked',
					currentPage: currentPageNumber,
				});

				const adviceStepContainer = getAdviceStepContainer(form, app.config);
				if (adviceStepContainer) {
					renderLoading(adviceStepContainer, app.config);
				}

				syncAdviceStepNextState(form, app.config);

				app.logger.debug('Boundary next clicked; primed machine advice loading state while waiting for advice result page.', {
					formId: app.config.formId,
					currentPage: currentPageNumber,
				});
				return;
			}

			app.logger.debug('Skipped machine advice hook: next click was outside boundary/advice pages.', {
				formId: app.config.formId,
				currentPage: currentPageNumber,
			});
		},
		true
	);

	const handleInvalidate = (event) => {
		invalidateAdviceIfNeeded(app, form, event.target, trackedNames);
	};

	form.addEventListener('input', handleInvalidate, true);
	form.addEventListener('change', handleInvalidate, true);
	form.dataset.sdConfigBoundaryBound = '1';

	app.logger.debug('Boundary advice fetch handlers attached.', {
		formId: app.config.formId,
		boundaryClass: app.config.boundaryClass,
		trackedInputCount: trackedNames.size,
	});
}
